import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Loader2Icon, MicIcon, SquareIcon, Trash2Icon } from "lucide-react";
import { cn } from "../../../utils";
/**
 * The two faces of the chat composer's voice affordance: the microphone that
 * starts a recording, and the bar that replaces the text input while one runs.
 *
 * Both are presentational — `useVoiceInput` owns the recorder and the
 * transcription — and both default their strings to English like this
 * package's other icon-only affordances; a translated surface passes its own.
 */ /** Format seconds as m:ss */ function formatDuration(seconds) {
    const minutes = Math.floor(seconds / 60);
    const remainder = seconds % 60;
    return `${minutes}:${remainder.toString().padStart(2, "0")}`;
}
const MIME_EXTENSIONS = [
    [
        "mp4",
        "m4a"
    ],
    [
        "m4a",
        "m4a"
    ],
    [
        "ogg",
        "ogg"
    ],
    [
        "wav",
        "wav"
    ],
    [
        "mpeg",
        "mp3"
    ],
    [
        "flac",
        "flac"
    ]
];
/**
 * The filename a recorded blob is uploaded under.
 *
 * Transcription APIs route on the extension, so it has to describe the
 * container the browser actually produced (Safari records mp4, Chrome webm).
 */ export function audioBlobFilename(blob) {
    const match = MIME_EXTENSIONS.find(([fragment])=>blob.type.includes(fragment));
    return `recording.${match?.[1] ?? "webm"}`;
}
/** Mic button shown when not recording */ export function VoiceInputIdle({ onStart, disabled, label = "Start voice recording" }) {
    return /*#__PURE__*/ _jsx("button", {
        type: "button",
        "data-slot": "ai-chat-voice-input-idle",
        onClick: onStart,
        disabled: disabled,
        "aria-label": label,
        title: label,
        className: cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-full", "transition-colors duration-200", disabled ? "text-muted-foreground cursor-not-allowed opacity-50" : "text-muted-foreground hover:text-foreground hover:bg-muted"),
        children: /*#__PURE__*/ _jsx(MicIcon, {
            className: "h-4 w-4"
        })
    });
}
/**
 * Recording bar overlay — replaces the text input while recording.
 * Shows pulsing indicator, audio visualization, elapsed time,
 * cancel button, and stop button.
 */ export function RecordingBar({ elapsed, isTranscribing, onStop, onCancel, stopLabel = "Stop recording", cancelLabel = "Cancel recording", transcribingLabel = "Transcribing..." }) {
    if (isTranscribing) {
        return /*#__PURE__*/ _jsxs("div", {
            "data-slot": "ai-chat-voice-input-transcribing",
            className: "flex flex-1 items-center justify-center gap-2",
            children: [
                /*#__PURE__*/ _jsx(Loader2Icon, {
                    className: "text-muted-foreground h-4 w-4 animate-spin"
                }),
                /*#__PURE__*/ _jsx("span", {
                    className: "text-muted-foreground text-sm",
                    children: transcribingLabel
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        "data-slot": "ai-chat-voice-input-recording",
        className: "flex flex-1 items-center gap-2",
        children: [
            /*#__PURE__*/ _jsx("button", {
                type: "button",
                onClick: onCancel,
                "aria-label": cancelLabel,
                title: cancelLabel,
                className: cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-full", "text-muted-foreground hover:text-destructive hover:bg-destructive/10", "transition-colors duration-200"),
                children: /*#__PURE__*/ _jsx(Trash2Icon, {
                    className: "h-4 w-4"
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-1 items-center gap-2.5 overflow-hidden",
                children: [
                    /*#__PURE__*/ _jsxs("span", {
                        className: "relative flex h-2.5 w-2.5 shrink-0",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "bg-destructive absolute inline-flex h-full w-full animate-ping rounded-full opacity-75"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "bg-destructive relative inline-flex h-2.5 w-2.5 rounded-full"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex items-center gap-[3px]",
                        "aria-hidden": "true",
                        children: Array.from({
                            length: 16
                        }).map((_, index)=>/*#__PURE__*/ _jsx("span", {
                                className: "animate-voice-bar bg-destructive/60 h-1 w-[2px] rounded-full",
                                style: {
                                    animationDelay: `${index * 0.05}s`
                                }
                            }, index))
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "text-muted-foreground ml-auto shrink-0 text-xs tabular-nums",
                        children: formatDuration(elapsed)
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("button", {
                type: "button",
                onClick: onStop,
                "aria-label": stopLabel,
                title: stopLabel,
                className: cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-full", "bg-destructive text-white", "hover:bg-destructive/90 transition-colors duration-200"),
                children: /*#__PURE__*/ _jsx(SquareIcon, {
                    className: "h-3 w-3 fill-current"
                })
            })
        ]
    });
}

//# sourceMappingURL=ai-chat-voice-input.js.map